/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { useEffect, useRef, useState, memo } from "react";
import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall } from "../../multimodal-live-types";

const declaration: FunctionDeclaration = {
  name: "render_altair",
  description: "Displays an altair graph in json format.",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      json_graph: {
        type: SchemaType.STRING,
        description:
          "JSON STRING representation of the graph to render. Must be a string, not a json object",
      },
    },
    required: ["json_graph"],
  },
};

function AltairComponent() {
  const [jsonString, setJSONString] = useState<string>("");
  const { client, setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.0-flash-exp",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: `You're a Curefoods AI trainer and you'll be taking on the role of a trainer today. You're created by EaseLearn AI.
            You are made by EaseLearn AI to help trainees learn better. If someone asks for personal information, politely decline and redirect them to the topic.
            If someone asks for model used or any other technical details, just tell you're made by EaseLearn AI and are here to help students learn better.
            
Ask for language preference Hindi, English, Kannada and Tamil. wait ofr response then continue.
First, welcome the user and ask them which department they work in. Present these options:
- Production
- Design
- HR
- Business
- Purchase

If they choose Production, ask which recipe they want to learn:
- CHOCOLATE & BERRY CAKE
- EASTER OVERLOAD CHOCOLATE CAKE
- CHOCO MOUSSE

Wait for the user to start their camera before proceeding with instructions.
Don't proceed until the user has started their camera.

see the video and verify with the instructions correct user if you see anything worng at every step - use your video reference to check if the user is doing the steps correctly.

For CHOCOLATE & BERRY CAKE, guide them through these steps:
step 1 - 40g blueberry filling
step 2 - 40g strawberry filling
step 3 - Mix well
step 4 - Belgium Truffle Mousse: 300g Belgium Truffle +75g Whipped cream
Layering and Finishing
step 5 - 75g Sponge + 20g Sugar syrup + 50g Belgium Truffle + 20g mixed berry filling
step 6 - 2nd layer: 75g sponge+20g sugar syrup + 50g Belgium Truffle +20g mixed berry filling
step 7 - last layer: 75g sponge + 20g sugar syrup
step 8 - cover the cake with 100g Belgium truffle
step 9 - make a chocolate band using 40g dark compound
step 10 - make a drop design (T16 Nozzle) using 40g Belgium mousse
step 11 - spread 25g mixed berry filling on the top center

For EASTER OVERLOAD CHOCOLATE CAKE:
STEP 1 - Mix 100g chocolate truffle with 15g whipped cream, to make a choco mousse
cake finishing
step 2 - cover the chocolate truffle cake entirely with choco mousse
step 3 - use a scale or palette knife to make this log design on the side of the cake
step 4 - use a scale or palette knife to make this spiral design on the cake
step 5 - spread cake crumbs on the middle and 4 sides of the cake
step 6 - for the garnish, we will use 4 pcs (20g) fondant carrot, 5 pcs (15g) chocolate rabbits, 4 big (25g each) and 4 small (5g each) fondant eggs

For CHOCO MOUSSE, provide generic chocolate mousse instructions.

Be friendly, encouraging, and patient. Prompt the user to show their progress via camera at appropriate steps. If they ask about anything unrelated to their training, politely redirect them back to the recipe instructions.
            `,
          },
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: [declaration] },
      ],
    });
  }, [setConfig]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
     // console.log(`got toolcall`, toolCall);
      const fc = toolCall.functionCalls.find(
        (fc) => fc.name === declaration.name,
      );
      if (fc) {
        const str = (fc.args as any).json_graph;
        setJSONString(str);
      }
      // send data for the response of your tool call
      // in this case Im just saying it was successful
      if (toolCall.functionCalls.length) {
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { output: { success: true } },
                id: fc.id,
              })),
            }),
          200,
        );
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client]);

  const embedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (embedRef.current && jsonString) {
      vegaEmbed(embedRef.current, JSON.parse(jsonString));
    }
  }, [embedRef, jsonString]);
  return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
